import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface TokenUsageData {
  stage: string;
  inputTokens: number;
  outputTokens: number;
}

interface TokenUsageChartProps {
  data: TokenUsageData[];
  totalInputTokens: number;
  totalOutputTokens: number;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function TokenUsageChart({
  data,
  totalInputTokens,
  totalOutputTokens,
}: TokenUsageChartProps) {
  const totalTokens = totalInputTokens + totalOutputTokens;

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Total Tokens</div>
          <div className="text-parchment text-lg font-medium">{formatNumber(totalTokens)}</div>
        </div>
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Input</div>
          <div className="text-cyan-400 text-lg font-medium">{formatNumber(totalInputTokens)}</div>
        </div>
        <div className="bg-void/60 rounded-lg p-3">
          <div className="text-mist/70 text-xs">Output</div>
          <div className="text-amber-400 text-lg font-medium">{formatNumber(totalOutputTokens)}</div>
        </div>
      </div>

      {/* Chart */}
      {data.length > 0 && (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
              <XAxis type="number" stroke="#707070" fontSize={10} />
              <YAxis
                dataKey="stage"
                type="category"
                stroke="#707070"
                fontSize={10}
                width={75}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: '#e8dcc4' }}
                itemStyle={{ color: '#a0a0a0' }}
                formatter={(value: number) => formatNumber(value)}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px' }}
                formatter={(value) => <span className="text-mist">{value}</span>}
              />
              <Bar dataKey="inputTokens" name="Input" fill="#22d3ee" radius={[0, 4, 4, 0]} />
              <Bar dataKey="outputTokens" name="Output" fill="#fbbf24" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
